import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Save, Coins, History, Diamond, Tag as TagIcon } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { SampleLabel } from "@/pages/admin/SampleLabel";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { hasAdminSession } from "@/lib/session";

// Renders the rate-change indicator: neutral dash for no change, green/red badge otherwise.
function GoldChangeBadge({ change }) {
  if (change === 0 || change === undefined) return <span className="text-muted-foreground text-sm">—</span>;
  if (change > 0) return <Badge className="bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]">+{formatINR(change)}</Badge>;
  return <Badge className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]">{formatINR(change)}</Badge>;
}

export default function Rates() {
  const navigate = useNavigate();
  const [rates, setRates] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState(null);
  const [savingRules, setSavingRules] = useState(false);
  const [label, setLabel] = useState(null);
  const [savingLabel, setSavingLabel] = useState(false);

  const loadHistory = () => api.get("/admin/rate-history").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    api.get("/admin/rates").then(({ data }) => setRates(data));
    api.get("/admin/pricing-rules").then(({ data }) => setRules(data)).catch(() => setRules({ labour: { min_charge: 0, threshold_weight_g: 0, per_gram_above: 0 }, hallmark: { min_per_piece: 0, per_carat: 0 } }));
    api.get("/admin/label-settings").then(({ data }) => setLabel(data)).catch(() => {});
    loadHistory();
    // eslint-disable-next-line
  }, []);

  if (!rates) return <AdminShell title="Daily Rates"><div className="py-24 text-center text-muted-foreground">Loading...</div></AdminShell>;

  const setDiamond = (q, v) => setRates({ ...rates, diamond_rates: { ...rates.diamond_rates, [q]: Number(v) } });
  const setStone = (q, v) => setRates({ ...rates, stone_rates: { ...rates.stone_rates, [q]: Number(v) } });
  const addRow = (key) => { const name = prompt(`New ${key === "diamond_rates" ? "diamond quality" : "colour stone"} name`); if (name) setRates({ ...rates, [key]: { ...rates[key], [name]: 0 } }); };
  const removeRow = (key, q) => { const copy = { ...rates[key] }; delete copy[q]; setRates({ ...rates, [key]: copy }); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/rates", {
        gold_rate_per_gram_24k: Number(rates.gold_rate_per_gram_24k),
        diamond_rates: rates.diamond_rates, stone_rates: rates.stone_rates, gst_pct: Number(rates.gst_pct),
      });
      toast.success("Rates updated — all product prices recalculated");
      loadHistory();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const saveRules = async () => {
    if (!rules) return;
    setSavingRules(true);
    try {
      const payload = {
        labour: {
          min_charge: Number(rules.labour?.min_charge || 0),
          threshold_weight_g: Number(rules.labour?.threshold_weight_g || 0),
          per_gram_above: Number(rules.labour?.per_gram_above || 0),
        },
        hallmark: {
          min_per_piece: Number(rules.hallmark?.min_per_piece || 0),
          per_carat: Number(rules.hallmark?.per_carat || 0),
        },
      };
      const { data } = await api.put("/admin/pricing-rules", payload);
      setRules(data.pricing_rules || payload);
      toast.success("Labour & Hallmark rules saved");
    } catch { toast.error("Save failed"); } finally { setSavingRules(false); }
  };

  const saveLabel = async () => {
    if (!label) return;
    setSavingLabel(true);
    try {
      // The CD410 layout is fixed at 100 x 15 mm with set field positions, so the
      // legacy size / position values are passed through untouched — they're
      // still part of the saved record but no longer drive the layout.
      const payload = {
        width_in: Number(label.width_in || 1.5),
        height_in: Number(label.height_in || 1.0),
        show_barcode: true,
        show_sku: !!label.show_sku,
        show_gross: !!label.show_gross,
        show_net: !!label.show_net,
        show_purity: !!label.show_purity,
        show_diamond: !!label.show_diamond,
        show_price: !!label.show_price,
        show_huid: !!label.show_huid,
        show_certificate: !!label.show_certificate,
        header_text: label.header_text || "",
        footer_text: "",
        font_scale: Number(label.font_scale || 1.0),
        positions: label.positions || undefined,
      };
      const { data } = await api.put("/admin/label-settings", payload);
      setLabel(data.label_settings || payload);
      toast.success("Label settings saved — applies to next print run");
    } catch { toast.error("Save failed"); } finally { setSavingLabel(false); }
  };

  return (
    <AdminShell title="Daily Rates">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><Coins className="h-5 w-5 text-[#8E6F33]" /><h3 className="font-medium">Gold & Tax</h3></div>
          <div className="space-y-3">
            <div><Label>Gold Rate per gram (24K)</Label><Input data-testid="rate-gold-input" type="number" value={rates.gold_rate_per_gram_24k} onChange={(e) => setRates({ ...rates, gold_rate_per_gram_24k: e.target.value })} className="mt-1 font-mono" /></div>
            <div><Label>GST %</Label><Input data-testid="rate-gst-input" type="number" value={rates.gst_pct} onChange={(e) => setRates({ ...rates, gst_pct: e.target.value })} className="mt-1 font-mono" /></div>
            <p className="text-xs text-muted-foreground">Last updated: {rates.effective_date ? new Date(rates.effective_date).toLocaleString("en-IN") : "-"}</p>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4"><div className="flex items-center gap-2"><Diamond className="h-5 w-5 text-[hsl(var(--primary))]" /><h3 className="font-medium">Diamond Rates</h3></div><Button size="sm" variant="secondary" onClick={() => navigate("/admin/diamond-rates")} className="gap-1">Open Diamond Rates</Button></div>
          <div className="rounded-lg border border-border bg-[#F7F0E6] p-4 text-sm text-muted-foreground">
            Diamond pricing now uses an editable <strong>Size × Grade</strong> matrix with separate <strong>Natural</strong> and <strong>Lab-Grown</strong> sections.
            Manage all diamond rates in the dedicated <button type="button" onClick={() => navigate("/admin/diamond-rates")} className="text-[hsl(var(--primary))] underline underline-offset-2">Diamond Rates</button> page.
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4"><h3 className="font-medium">Colour Stone Rates (per carat)</h3><Button size="sm" variant="secondary" onClick={() => addRow("stone_rates")} className="gap-1"><Plus className="h-3.5 w-3.5" /> Add</Button></div>
          <div className="space-y-2">
            {Object.entries(rates.stone_rates).map(([q, v]) => (
              <div key={q} className="flex items-center gap-2"><span className="w-24 text-sm font-medium">{q}</span><Input type="number" value={v} onChange={(e) => setStone(q, e.target.value)} className="font-mono" /><Button size="icon" variant="ghost" onClick={() => removeRow("stone_rates", q)}><Trash2 className="h-4 w-4" /></Button></div>
            ))}
          </div>
        </Card>

        <div className="lg:col-span-2">
          <Button data-testid="admin-rates-save-button" onClick={save} disabled={saving} className="gap-2 h-12 w-full sm:w-auto"><Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Rates & Recalculate Prices"}</Button>
        </div>

        <div className="lg:col-span-2">
          <Card className="p-6" data-testid="pricing-rules-card">
            <div className="flex items-center gap-2 mb-1"><Coins className="h-5 w-5 text-[#8E6F33]" /><h3 className="font-medium">Labour & Hallmark / IGI Rules (fallback)</h3></div>
            <p className="text-xs text-muted-foreground mb-4">Applied automatically to any product that does NOT carry its own Making or Hallmark charge. Products priced by hand are unaffected — this is a fallback, not an override.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="text-sm font-medium">Labour (Making)</div>
                <div>
                  <Label className="text-xs">Minimum charge (₹)</Label>
                  <Input data-testid="labour-min-charge" type="number" value={rules?.labour?.min_charge ?? 0} onChange={(e) => setRules({ ...rules, labour: { ...(rules?.labour || {}), min_charge: e.target.value } })} className="mt-1 font-mono" />
                </div>
                <div>
                  <Label className="text-xs">Applies up to weight (g)</Label>
                  <Input data-testid="labour-threshold" type="number" value={rules?.labour?.threshold_weight_g ?? 0} onChange={(e) => setRules({ ...rules, labour: { ...(rules?.labour || {}), threshold_weight_g: e.target.value } })} className="mt-1 font-mono" />
                </div>
                <div>
                  <Label className="text-xs">Per gram above threshold (₹/g)</Label>
                  <Input data-testid="labour-per-gram" type="number" value={rules?.labour?.per_gram_above ?? 0} onChange={(e) => setRules({ ...rules, labour: { ...(rules?.labour || {}), per_gram_above: e.target.value } })} className="mt-1 font-mono" />
                </div>
                <p className="text-xs text-muted-foreground">Example: min ₹2000 up to 2g, ₹1000/g above → 1g = ₹2000, 2g = ₹2000, 2.5g = ₹2500, 3g = ₹3000.</p>
              </div>
              <div className="space-y-3">
                <div className="text-sm font-medium">Hallmark / IGI Certification</div>
                <div>
                  <Label className="text-xs">Minimum per piece (₹)</Label>
                  <Input data-testid="hallmark-min" type="number" value={rules?.hallmark?.min_per_piece ?? 0} onChange={(e) => setRules({ ...rules, hallmark: { ...(rules?.hallmark || {}), min_per_piece: e.target.value } })} className="mt-1 font-mono" />
                </div>
                <div>
                  <Label className="text-xs">Per carat add-on (₹/ct, optional)</Label>
                  <Input data-testid="hallmark-per-carat" type="number" value={rules?.hallmark?.per_carat ?? 0} onChange={(e) => setRules({ ...rules, hallmark: { ...(rules?.hallmark || {}), per_carat: e.target.value } })} className="mt-1 font-mono" />
                </div>
                <p className="text-xs text-muted-foreground">Applies to pieces without their own hallmarking charge. Uses total diamond+stone carat when a per-carat value is set.</p>
              </div>
            </div>
            <div className="mt-4">
              <Button data-testid="save-pricing-rules-button" onClick={saveRules} disabled={savingRules} className="gap-2"><Save className="h-4 w-4" /> {savingRules ? "Saving..." : "Save Labour & Hallmark Rules"}</Button>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card className="p-6" data-testid="label-settings-card">
            <div className="flex items-center gap-2 mb-1"><TagIcon className="h-5 w-5 text-[hsl(var(--primary))]" /><h3 className="font-medium">Label Settings — CD410 Hang Tag</h3></div>
            <p className="text-xs text-muted-foreground mb-4">Fixed 100 × 15 mm hang tag (printable area 0–55 mm): the <b>front</b> (0–27.5 mm) carries the weights, the <b>back</b> (27.5–55 mm) carries your shop name, a QR code of the piece ID and the identity details, and the <b>tail</b> (55–100 mm) stays blank. Choose which fields print below — applies to the next Print Tags run.</p>
            {!label ? <div className="text-sm text-muted-foreground">Loading…</div> : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="space-y-3">
                    <div className="text-sm font-medium">Back panel — shop name</div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <Label className="text-xs">Shop name (top of back panel)</Label>
                        <Input data-testid="label-header-input" value={label.header_text ?? ""} onChange={(e) => setLabel((prev) => ({ ...prev, header_text: e.target.value }))} placeholder="e.g. MAAHEY" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Font scale</Label>
                        <Input data-testid="label-scale-input" type="number" step="0.1" min="0.6" max="1.4" value={label.font_scale ?? 1.0} onChange={(e) => setLabel((prev) => ({ ...prev, font_scale: e.target.value }))} className="mt-1 font-mono" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="text-sm font-medium">Fields to print</div>
                    <p className="text-xs text-muted-foreground -mt-2">Item name and the QR code always print on the back. On the front, St.Wt (stone), D.Wt (diamond carats) and D.Quality print automatically whenever the piece or its design has those values.</p>
                    {[
                      { key: "show_purity", label: "Purity (next to item name)" },
                      { key: "show_sku", label: "SKU" },
                      { key: "show_gross", label: "G.Wt (gross weight)" },
                      { key: "show_net", label: "N.Wt (net weight)" },
                      { key: "show_huid", label: "HUID (BIS hallmark)" },
                      { key: "show_price", label: "Selling price" },
                    ].map((f) => (
                      <div key={f.key} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                        <span className="text-sm">{f.label}</span>
                        <Switch data-testid={`label-toggle-${f.key}`} checked={!!label[f.key]} onCheckedChange={(v) => setLabel((prev) => ({ ...prev, [f.key]: v }))} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2 lg:sticky lg:top-4 lg:self-start">
                  <div className="text-sm font-medium self-start">Live Preview</div>
                  {/* Live tag preview — re-renders on every keystroke so jewellers
                      see fields appear or drop as they toggle them. */}
                  <div className="p-4 rounded-lg bg-[#F7F0E6] overflow-auto max-w-full flex justify-center">
                    <div className="shadow-md"><SampleLabel settings={label} showGuides /></div>
                  </div>
                  <p className="text-xs text-muted-foreground text-center">Rendered at true physical size (100 × 15 mm). Dashed fold guides are on-screen only — the printed tag has no borders or lines.</p>
                </div>
              </div>
            )}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Button data-testid="save-label-settings-button" onClick={saveLabel} disabled={savingLabel || !label} className="gap-2"><Save className="h-4 w-4" /> {savingLabel ? "Saving..." : "Save Label Settings"}</Button>
              <Button data-testid="label-settings-preview" variant="outline" className="gap-2" onClick={() => window.open("/admin/print-tags?barcodes=PREVIEW-EXAMPLE-000001", "_blank", "noopener")}>
                <TagIcon className="h-4 w-4" /> Preview Sample Tag
              </Button>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4"><History className="h-5 w-5 text-[hsl(var(--primary))]" /><h3 className="font-medium">Rate Change History</h3></div>
            <div className="overflow-x-auto">
              <Table data-testid="rate-history-table">
                <TableHeader><TableRow className="bg-[#F4EBDD]">
                  <TableHead>Date & Time</TableHead><TableHead>Gold 24K (/g)</TableHead><TableHead>Change</TableHead><TableHead>GST %</TableHead><TableHead>By</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="text-sm">{new Date(h.created_at).toLocaleString("en-IN")}</TableCell>
                      <TableCell className="font-mono">{formatINR(h.gold_rate_per_gram_24k)}</TableCell>
                      <TableCell>
                        <GoldChangeBadge change={h.gold_change} />
                      </TableCell>
                      <TableCell className="font-mono">{h.gst_pct}%</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{h.changed_by || "admin"}</TableCell>
                    </TableRow>
                  ))}
                  {history.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No rate changes logged yet. Save a rate update to start tracking.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}
